import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { deleteTeam, getTeams, saveTeam } from './teamApi';

const selectClass =
  'h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60';

const errorMessage = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

// A team is picked by what each person's role can see, so custom roles count too.
// An older server sends no scope, and then only the two built-in roles qualify.
const scopeOf = (person) => person.scope || { hr_manager: 'team', recruiter: 'assigned_jobs' }[person.role?.key];
const nameWithRole = (person) => (person.role?.name ? `${person.name} · ${person.role.name}` : person.name);

const TeamForm = ({ team, managers, recruiters, onSaved, onCancel }) => {
  const [name, setName] = useState(team?.name || '');
  const [managerId, setManagerId] = useState(team?.manager_user_id ? String(team.manager_user_id) : '');
  const [members, setMembers] = useState(() => new Set(team?.member_ids || []));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggle = (id) => setMembers((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name the team.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await saveTeam({ id: team?.id, name: name.trim(), managerUserId: managerId ? Number(managerId) : null, memberIds: [...members] });
      toast.success(team ? 'Team updated' : 'Team created');
      await onSaved();
    } catch (err) {
      setError(errorMessage(err, 'Could not save the team.'));
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" noValidate>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor="team-name" className="text-xs">Team name</Label>
          <Input id="team-name" className="h-9" value={name} autoFocus disabled={saving}
            onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="team-manager" className="text-xs">Manager</Label>
          <select id="team-manager" className={selectClass} value={managerId} disabled={saving}
            onChange={(e) => setManagerId(e.target.value)}>
            <option value="">No manager yet</option>
            {managers.map((person) => <option key={person.id} value={person.id}>{nameWithRole(person)}</option>)}
          </select>
        </div>
      </div>
      <fieldset className="mt-3">
        <legend className="text-xs font-medium text-foreground">Members</legend>
        {recruiters.length === 0 ? (
          <p className="mt-1 text-xs text-muted-foreground">
            Nobody on your team has a role that works on assigned jobs, such as Recruiter.
          </p>
        ) : (
          <div className="mt-1 grid gap-1 sm:grid-cols-2">
            {recruiters.map((person) => (
              <label key={person.id} className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 text-sm hover:bg-white">
                <input type="checkbox" className="h-4 w-4 accent-primary" checked={members.has(person.id)}
                  onChange={() => toggle(person.id)} disabled={saving} />
                <span className="truncate">{nameWithRole(person)}</span>
              </label>
            ))}
          </div>
        )}
      </fieldset>
      <p className="mt-2 text-xs text-muted-foreground">
        The manager sees every job their team&apos;s members work on. Managers are people on a role that sees its
        team&apos;s jobs, such as HR Manager.
      </p>
      {error ? (
        <div role="alert" className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      ) : null}
      <div className="mt-3 flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="submit" size="sm" disabled={saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {team ? 'Save team' : 'Create team'}
        </Button>
      </div>
    </form>
  );
};

/** Settings → Team & Roles → Teams: one HR Manager over a group of Recruiters. */
export default function TeamsTab() {
  const [teams, setTeams] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await getTeams();
      setTeams(data.teams);
      setPeople(data.people);
      setError('');
    } catch (err) {
      setError(errorMessage(err, 'Could not load teams.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const names = useMemo(() => new Map(people.map((person) => [person.id, person.name])), [people]);
  const managers = people.filter((person) => scopeOf(person) === 'team');
  const recruiters = people.filter((person) => scopeOf(person) === 'assigned_jobs');

  const afterSave = async () => {
    setEditing(null);
    await load();
  };

  const confirmDelete = async () => {
    const team = deleting;
    setDeleting(null);
    try {
      await deleteTeam(team.id);
      toast.success(`${team.name} was deleted`);
      await load();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not delete the team.'));
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Group people under a manager, who then sees the jobs their team works on.
        </p>
        {editing === null ? (
          <Button type="button" size="sm" onClick={() => setEditing('new')}>
            <Plus className="mr-1.5 h-4 w-4" />
            New team
          </Button>
        ) : null}
      </div>

      {editing === 'new' ? (
        <TeamForm managers={managers} recruiters={recruiters} onSaved={afterSave} onCancel={() => setEditing(null)} />
      ) : null}

      {error ? (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      ) : null}

      <div className="rounded-lg border border-slate-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading teams" />
          </div>
        ) : teams.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground">No teams yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {teams.map((team) => (
              <li key={team.id} className="px-4 py-2.5">
                {editing === team.id ? (
                  <TeamForm team={team} managers={managers} recruiters={recruiters} onSaved={afterSave}
                    onCancel={() => setEditing(null)} />
                ) : (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <div className="min-w-0 flex-1 basis-48">
                      <div className="truncate text-sm font-medium text-slate-900">{team.name}</div>
                      <div className={cn('truncate text-xs', team.manager_user_id ? 'text-muted-foreground' : 'text-amber-700')}>
                        {team.manager_user_id ? `Managed by ${names.get(team.manager_user_id) || 'a former member'}` : 'No manager yet'}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 basis-48 truncate text-xs text-muted-foreground">
                      {team.member_ids.length === 0
                        ? 'No members'
                        : team.member_ids.map((id) => names.get(id) || 'Former member').join(', ')}
                    </div>
                    <div className="ml-auto flex gap-1">
                      <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(team.id)}>Edit</Button>
                      <Button type="button" variant="ghost" size="sm" className="text-slate-500 hover:text-red-600"
                        onClick={() => setDeleting(team)}>Delete</Button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <AlertDialog open={Boolean(deleting)} onOpenChange={(open) => { if (!open) setDeleting(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Its manager stops seeing the jobs its members work on. Nobody loses their own jobs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete team</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
